import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import AddToEHR from '../components/AddToEHR';
import { 
  Video, Phone, PhoneOff, Mic, MicOff, VideoOff, Monitor, 
  Users, Calendar, Clock, ArrowLeft, RefreshCw, Plus, Loader2,
  PhoneCall, PhoneIncoming, PhoneMissed, CheckCircle, XCircle,
  User, Settings, Volume2, VolumeX, Maximize, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoCallCenter() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [upcomingCalls, setUpcomingCalls] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [iceServers, setIceServers] = useState([]);
  
  // Call state
  const [inCall, setInCall] = useState(false);
  const [currentCall, setCurrentCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle');
  
  // Media state
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  
  // Dialogs
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showCallDialog, setShowCallDialog] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  
  // Refs
  const localVideoRef = useRef(null);
  const peerConnections = useRef({});
  const wsRef = useRef(null);
  
  const token = localStorage.getItem('hospice_token');

  const fetchData = useCallback(async () => {
    try {
      const [upcoming, recent, ice] = await Promise.all([
        fetch(`${API_URL}/api/video/scheduled/upcoming`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()),
        fetch(`${API_URL}/api/video/history/recent`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()),
        fetch(`${API_URL}/api/video/ice-servers`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json())
      ]);
      
      setUpcomingCalls(upcoming.calls || []);
      setRecentCalls(recent.calls || []);
      setIceServers(ice.ice_servers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startLocalVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (error) {
      toast.error('Failed to access camera/microphone');
      console.error('Media error:', error);
      return null;
    }
  };

  const stopLocalVideo = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setAudioEnabled(!audioEnabled);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setVideoEnabled(!videoEnabled);
    }
  };

  const initiateCall = async (participantIds, callType = 'one_on_one', title = null) => {
    try {
      const stream = await startLocalVideo();
      if (!stream) return;
      
      const response = await fetch(`${API_URL}/api/video/initiate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participant_ids: participantIds,
          call_type: callType,
          title: title
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setCurrentCall(data);
        setInCall(true);
        setCallStatus('ringing');
        toast.success('Calling...');
        
        // Connect to WebSocket for signaling
        connectToCallWebSocket(data.call_id);
      } else {
        stopLocalVideo();
        toast.error('Failed to initiate call');
      }
    } catch (error) {
      stopLocalVideo();
      toast.error('Failed to initiate call');
      console.error(error);
    }
  };

  const answerCall = async (callId) => {
    try {
      const stream = await startLocalVideo();
      if (!stream) return;
      
      const response = await fetch(`${API_URL}/api/video/${callId}/answer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setCurrentCall(data);
        setInCall(true);
        setCallStatus('active');
        setIncomingCall(null);
        
        connectToCallWebSocket(callId);
      }
    } catch (error) {
      stopLocalVideo();
      toast.error('Failed to answer call');
    }
  };

  const rejectCall = async (callId) => {
    try {
      await fetch(`${API_URL}/api/video/${callId}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomingCall(null);
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  };

  const endCall = async () => {
    if (currentCall) {
      try {
        await fetch(`${API_URL}/api/video/${currentCall.call_id}/end`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error ending call:', error);
      }
    }
    
    // Clean up
    stopLocalVideo();
    Object.values(peerConnections.current).forEach(pc => pc.close());
    peerConnections.current = {};
    setRemoteStreams({});
    setInCall(false);
    setCurrentCall(null);
    setCallStatus('idle');
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    fetchData();
  };

  const connectToCallWebSocket = (callId) => {
    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/video/ws/${callId}?token=${token}`;
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('WebSocket connected for call:', callId);
    };
    
    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'webrtc_signal':
          await handleWebRTCSignal(data);
          break;
        case 'video_participant_joined':
          toast.info(`${data.user_id} joined the call`);
          break;
        case 'video_participant_left':
          toast.info(`${data.user_id} left the call`);
          break;
        case 'call_ended':
          toast.info('Call ended');
          endCall();
          break;
        default:
          console.log('Unknown message type:', data.type);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };
  };

  const handleWebRTCSignal = async (data) => {
    const { signal_type, from_user, data: signalData } = data;
    
    if (signal_type === 'offer') {
      // Create peer connection and answer
      const pc = createPeerConnection(from_user);
      await pc.setRemoteDescription(new RTCSessionDescription(signalData.sdp));
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      wsRef.current?.send(JSON.stringify({
        type: 'answer',
        target: from_user,
        sdp: answer
      }));
    } else if (signal_type === 'answer') {
      const pc = peerConnections.current[from_user];
      if (pc) {
        await pc.setRemoteDescription(new RTCSessionDescription(signalData.sdp));
      }
    } else if (signal_type === 'ice_candidate') {
      const pc = peerConnections.current[from_user];
      if (pc && signalData.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(signalData.candidate));
      }
    }
  };

  const createPeerConnection = (peerId) => {
    const pc = new RTCPeerConnection({ iceServers });
    peerConnections.current[peerId] = pc;
    
    // Add local tracks
    if (localStream) {
      localStream.getTracks().forEach(track => {
        pc.addTrack(track, localStream);
      });
    }
    
    // Handle remote tracks
    pc.ontrack = (event) => {
      setRemoteStreams(prev => ({
        ...prev,
        [peerId]: event.streams[0]
      }));
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        wsRef.current?.send(JSON.stringify({
          type: 'ice_candidate',
          target: peerId,
          candidate: event.candidate
        }));
      }
    };
    
    return pc;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ended': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'missed': return <PhoneMissed className="w-4 h-4 text-red-400" />;
      case 'active': return <Phone className="w-4 h-4 text-blue-400" />;
      default: return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Active call view
  if (inCall) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col" data-testid="video-call-active">
        {/* Call header */}
        <div className="bg-slate-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Video className="w-6 h-6 text-blue-400" />
            <div>
              <h2 className="text-white font-semibold">{currentCall?.title || 'Video Call'}</h2>
              <Badge className={callStatus === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}>
                {callStatus}
              </Badge>
            </div>
          </div>
          <Button variant="destructive" onClick={endCall}>
            <PhoneOff className="w-4 h-4 mr-2" /> End Call
          </Button>
        </div>
        
        {/* Video grid */}
        <div className="flex-1 p-4 grid grid-cols-2 gap-4">
          {/* Local video */}
          <div className="relative bg-slate-800 rounded-xl overflow-hidden">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <Badge className="bg-blue-500/80 text-white">You</Badge>
              {!audioEnabled && <MicOff className="w-4 h-4 text-red-400" />}
              {!videoEnabled && <VideoOff className="w-4 h-4 text-red-400" />}
            </div>
          </div>
          
          {/* Remote videos */}
          {Object.entries(remoteStreams).map(([peerId, stream]) => (
            <div key={peerId} className="relative bg-slate-800 rounded-xl overflow-hidden">
              <video
                autoPlay
                playsInline
                className="w-full h-full object-cover"
                ref={el => { if (el) el.srcObject = stream; }}
              />
              <div className="absolute bottom-4 left-4">
                <Badge className="bg-slate-700/80 text-white">{peerId}</Badge>
              </div>
            </div>
          ))}
          
          {/* Placeholder for waiting participants */}
          {Object.keys(remoteStreams).length === 0 && callStatus === 'ringing' && (
            <div className="bg-slate-800 rounded-xl flex items-center justify-center">
              <div className="text-center text-slate-400">
                <Loader2 className="w-12 h-12 mx-auto animate-spin mb-4" />
                <p>Waiting for participants...</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Call controls */}
        <div className="bg-slate-800 p-4 flex items-center justify-center gap-4">
          <Button
            variant={audioEnabled ? "outline" : "destructive"}
            size="icon"
            onClick={toggleAudio}
            className="w-12 h-12 rounded-full"
          >
            {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </Button>
          <Button
            variant={videoEnabled ? "outline" : "destructive"}
            size="icon"
            onClick={toggleVideo}
            className="w-12 h-12 rounded-full"
          >
            {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={() => toast.info('Screen sharing coming soon')}
          >
            <Monitor className="w-5 h-5" />
          </Button>
          <Button
            variant="destructive"
            size="icon"
            onClick={endCall}
            className="w-14 h-14 rounded-full"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="video-call-center">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900/50 to-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.location.href = '/dashboard'}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <Video className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Video Call Center</h1>
                <p className="text-slate-400 text-sm">Telehealth & Family Meetings</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AddToEHR 
                buttonVariant="default"
                buttonSize="default"
                buttonText="Add to EHR"
                className="bg-blue-600 hover:bg-blue-700"
              />
              <Button 
                onClick={() => setShowScheduleDialog(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" /> Schedule Call
              </Button>
              <Button 
                variant="outline"
                onClick={fetchData}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-300">Quick Call</p>
                  <p className="text-lg font-semibold text-white">Start Instant Call</p>
                </div>
                <Button 
                  size="icon" 
                  className="bg-green-600 hover:bg-green-700 w-12 h-12 rounded-full"
                  onClick={() => setShowCallDialog(true)}
                >
                  <PhoneCall className="w-6 h-6" />
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-300">Upcoming</p>
                  <p className="text-lg font-semibold text-white">{upcomingCalls.length} Scheduled</p>
                </div>
                <Calendar className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-300">ICE Servers</p>
                  <p className="text-lg font-semibold text-white">{iceServers.length} Configured</p>
                </div>
                <Settings className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 w-full justify-start mb-4">
            <TabsTrigger value="upcoming" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300">
              <Calendar className="w-4 h-4 mr-2" /> Upcoming
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-300">
              <Clock className="w-4 h-4 mr-2" /> Call History
            </TabsTrigger>
          </TabsList>

          {/* Upcoming Calls */}
          <TabsContent value="upcoming">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Upcoming Calls ({upcomingCalls.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingCalls.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Video className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                    <p>No scheduled calls</p>
                    <Button 
                      className="mt-4 bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowScheduleDialog(true)}
                    >
                      Schedule a Call
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingCalls.map((call) => (
                      <div key={call.id} className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{call.title}</p>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(call.scheduled_time).toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {call.participant_ids?.length || 0} participants
                            </span>
                          </div>
                        </div>
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => initiateCall(call.participant_ids, call.call_type, call.title)}
                        >
                          <Video className="w-4 h-4 mr-2" /> Join
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Call History */}
          <TabsContent value="history">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-400" />
                  Recent Calls ({recentCalls.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentCalls.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <PhoneOff className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                    <p>No call history</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentCalls.map((call) => (
                      <div key={call.id} className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(call.status)}
                          <div>
                            <p className="font-medium text-white">{call.title}</p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                              <span>{new Date(call.ended_at || call.started_at).toLocaleString()}</span>
                              {call.duration_seconds > 0 && (
                                <span>{formatDuration(call.duration_seconds)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Badge className={
                          call.status === 'ended' ? 'bg-green-500/20 text-green-300' :
                          call.status === 'missed' ? 'bg-red-500/20 text-red-300' :
                          'bg-slate-500/20 text-slate-300'
                        }>
                          {call.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Incoming Call Dialog */}
      <Dialog open={!!incomingCall} onOpenChange={() => setIncomingCall(null)}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <PhoneIncoming className="w-5 h-5 text-green-400 animate-pulse" />
              Incoming Call
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {incomingCall?.initiator_name} is calling you
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center gap-4 py-8">
            <Button
              variant="destructive"
              size="lg"
              onClick={() => rejectCall(incomingCall?.call_id)}
              className="w-20 h-20 rounded-full"
            >
              <PhoneOff className="w-8 h-8" />
            </Button>
            <Button
              size="lg"
              onClick={() => answerCall(incomingCall?.call_id)}
              className="w-20 h-20 rounded-full bg-green-600 hover:bg-green-700"
            >
              <Phone className="w-8 h-8" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Call Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Schedule Video Call</DialogTitle>
            <DialogDescription className="text-slate-400">
              Schedule a video call with patients or care team members
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Title</label>
              <Input placeholder="Call title..." className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Date & Time</label>
              <Input type="datetime-local" className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Notes</label>
              <Textarea placeholder="Add any notes..." className="bg-slate-700 border-slate-600 text-white" rows={3} />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => {
                toast.success('Call scheduled');
                setShowScheduleDialog(false);
                fetchData();
              }}>Schedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Call Dialog */}
      <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Start Quick Call</DialogTitle>
            <DialogDescription className="text-slate-400">
              Start an instant video call
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center py-8">
              <Video className="w-16 h-16 mx-auto text-blue-400 mb-4" />
              <p className="text-slate-300">Click below to start a test call</p>
              <p className="text-sm text-slate-500 mt-2">
                In production, you would select participants here
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCallDialog(false)}>Cancel</Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setShowCallDialog(false);
                  toast.info('Test call feature - select participants from the patient list');
                }}
              >
                <Video className="w-4 h-4 mr-2" /> Start Call
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
